import dayjs from 'utils/date';

import { User } from 'models/user/User';
import { Duration } from './Duration';
import { Image } from './Image';
import { Shipping } from './Shipping';

/**
 * - draft: 작성이 완료되지 않은 경매
 * - active: 시작된 경매
 * - sold: 정상적으로 종료되고, 상품이 판매된 경매
 * - unsold: 정상적으로 종료되었으나, 상품이 판매되지 않은 경매
 * - canceled: 사용자가 취소한 경매 (bid_count == 0 일 때에만 취소 가능)
 * - suspended: 정책 위반 등의 사유로 관리자가 강제로 중지한 경매
 */
export type AuctionState = 'draft' | 'active' | 'sold' | 'unsold' | 'canceled' | 'suspended';

interface AuctionStateDescription {
  name: string;
  description: string;
}

export const auctionStateDescription: Record<AuctionState, AuctionStateDescription> = {
  draft: {
    name: '초안',
    description: '현재 작성 중인 경매',
  },
  active: {
    name: '진행 중',
    description: '현재 진행 중인 경매',
  },
  sold: {
    name: '낙찰 종료',
    description: '낙찰되어 완료된 경매',
  },
  unsold: {
    name: '유찰 종료',
    description: '종료되었으나 낙찰되지 않은 경매',
  },
  canceled: {
    name: '취소',
    description: '판매자가 취소한 경매',
  },
  suspended: {
    name: '중지',
    description: '관리자가 강제로 중지한 경매',
  },
};

/**
 * - new: 미개봉 상품
 * - used: 중고 상품
 * - refurbished: 리퍼 상품
 * - other: 기타
 */
export type ProductCondition = 'new' | 'used' | 'refurbished' | 'other';

export const productConditionOptions = [
  { value: 'new', label: '미개봉' },
  { value: 'used', label: '중고' },
  { value: 'refurbished', label: '리퍼' },
  { value: 'other', label: '기타' },
];

export interface AuctionSummary {
  id: string;
  title: string;
  current_price: number;
  thumbnail_url: string;
  starts_at: string; // unix timestamp (ms unit)
  ends_at: string; // unix timestamp (ms unit)
}

export interface AuctionPatch {
  // related with auction
  title: string;
  description: string;
  duration: Duration;
  condition: ProductCondition;
  image_ids: string[];
  invitation_code_required: boolean;

  // related with trade
  shipping_methods: Shipping[];
  payment: string;
  return_policy: string;
}

export interface AuctionDraft extends Omit<AuctionPatch, 'image_ids'> {
  id: string;
  images: Image[];
}

export interface Auction extends AuctionDraft {
  // metadata
  state: AuctionState;
  has_invitation_code: boolean;
  invitation_code?: string;

  // image
  thumbnail_url: string;

  // related with bid
  bid_count: number;
  current_price: number;

  // related with time (unix timestamp) (ms unit)
  starts_at: string;
  ends_at: string;

  // marketplace
  marketplace_id: string;

  // seller
  seller: User;
}

export const hasBids = (auction: Auction) => {
  return auction.bid_count > 0;
};

export const isActiveAuction = (auction: Auction | AuctionSummary) => {
  return !isNotYetStartedAuction(auction) && !isEndedAuction(auction);
};
export const isNotYetStartedAuction = (auction: Auction | AuctionSummary) => {
  const startDateMS = +auction.starts_at;
  const startDate = dayjs.unix(startDateMS / 1000);
  const now = dayjs();

  return now.isBefore(startDate);
};

export const isEndedAuction = (auction: Auction | AuctionSummary) => {
  const endDateMS = +auction.ends_at;
  const endDate = dayjs.unix(endDateMS / 1000);
  const now = dayjs();

  return now.isAfter(endDate);
};

interface AuctionDateDescription {
  state: 'scheduled' | 'active' | 'finished';
  description: string;
}

export const getAuctionDateDescription = (auction: Auction | AuctionSummary): AuctionDateDescription => {
  const { starts_at, ends_at } = auction;

  const startDateMS = +starts_at;
  const endDateMS = +ends_at;

  const startDate = dayjs.unix(startDateMS / 1000);
  const endDate = dayjs.unix(endDateMS / 1000);

  let auctionDateDescription: AuctionDateDescription;
  if (isNotYetStartedAuction(auction)) {
    auctionDateDescription = {
      state: 'scheduled',
      description: `${startDate.fromNow()} 시작`,
    };
  } else if (isEndedAuction(auction)) {
    auctionDateDescription = {
      state: 'finished',
      description: `${endDate.fromNow()} 종료됨`,
    };
  } else {
    auctionDateDescription = {
      state: 'active',
      description: `${endDate.fromNow()} 종료 예정`,
    };
  }

  return auctionDateDescription;
};
